import { useCallback, useEffect, useRef, useState } from "react";
import { getClientStats } from "@/features/clients/api";
import type { ClientStats } from "@/features/clients/types";
import { ApiError } from "@/lib/api";

interface UseClientStatsResult {
  stats: ClientStats | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches the practice-wide summary-card counts. Kept separate from
 * `useClients` because stats ignore the list's query params and must be
 * refetchable independently after a delete/bulk/import. Same guards as
 * `useClients`: reload-key dedupe (collapses StrictMode's double invoke) and a
 * request-id check so a slow response can't clobber a newer one.
 */
export function useClientStats(firm?: string): UseClientStatsResult {
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const lastKeyRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const key = `${reloadKey}|${firm ?? ""}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    getClientStats(firm)
      .then((result) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setStats(result);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setError(err instanceof ApiError ? err.message : "Failed to load client stats.");
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [reloadKey, firm]);

  return { stats, loading, error, refetch };
}
